import { useState } from "react";
import { Dialog } from "@headlessui/react";
import { ArrowRightIcon, TagIcon } from "@heroicons/react/24/outline";
import FacilityAddress from "../../pages/status/components/facilityAddress";
import ShippedChoice from "../../pages/status/components/shippedChoice";
import TrackingEntryForm from "../../pages/status/components/trackingEntryForm";
import { useUpdateShippingStatus } from "../../hooks/useUpdateShippingStatus";
import { useNavigate } from "react-router-dom";
import { routeEndpoints } from "../../routes/routeEndpoints";

interface ShippingStatusModalProps {
  isOpen: boolean;
  orderId: string;
  onClose: () => void;
  onSubmitted: () => void;
}

const ShippingStatusModal = ({
  isOpen,
  orderId,
  onClose,
  onSubmitted,
}: ShippingStatusModalProps) => {
  const navigate = useNavigate();
  const [isShipped, setIsShipped] = useState(false);
  const updateMutation = useUpdateShippingStatus(orderId, {
    onSuccess: onSubmitted,
  });

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div
        className="fixed inset-0 bg-black/45 backdrop-blur-sm"
        aria-hidden="true"
      />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-150 max-h-full overflow-y-auto rounded-2xl bg-white md:p-8 p-5 shadow-xl space-y-6">
          <FacilityAddress />

          <div>
            <Dialog.Title className="md:text-2xl text-lg font-medium text-slate-800 mb-8 text-center">
              Have you already shipped your item?
            </Dialog.Title>
            <ShippedChoice isShipped={isShipped} onChange={setIsShipped} />

            {isShipped ? (
              <TrackingEntryForm
                orderId={orderId}
                submitLabel={
                  updateMutation.isPending ? (
                    "Submitting..."
                  ) : (
                    <>
                      Submit tracking & continue
                      <ArrowRightIcon className="size-5" />
                    </>
                  )
                }
                isSubmitting={updateMutation.isPending}
                onSubmit={(submission) => updateMutation.mutate(submission)}
              />
            ) : (
              <div className="flex flex-col md:gap-5 gap-3">
                <button
                  type="button"
                  onClick={() =>
                    navigate(`${routeEndpoints.shippingLabel}?id=${orderId}`)
                  }
                  className="h-13 w-full flex items-center justify-center gap-2 rounded-xl text-base font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
                >
                  <TagIcon className="size-5" />
                  Buy Shipping Label
                </button>
                <button
                  type="button"
                  onClick={onSubmitted}
                  className="h-13 w-full rounded-xl text-base font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 cursor-pointer transition-colors"
                >
                  Continue without shipping
                </button>
              </div>
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default ShippingStatusModal;
